import { useMemo, Dispatch, SetStateAction, JSX, Fragment } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { abbreviateNumber, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { MdPushPin } from 'react-icons/md';
import './MarketingOutcomes.scss';
import cn from 'classnames';
import { abbreviateScenarioValue } from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { PercentChange } from '@Components/PercentChange/PercentChange';
import { Size, StretchMode } from '@Components/designConstants';
import { PinnedSpendPointData } from '../../hroas-types';
import {
  TotalHolisticRevenueColor,
  ROASColor,
  AxisColor,
  GridColor,
  PinnedSpendLineColor
} from '../utils/colors';
import { MarketingOutcomesLegend } from './MarketingOutcomesLegend';
import {
  EfficientFrontierSeries,
  getPercentageDifferenceIndicator
} from '../utils/efficientFrontierUtils';

interface MarketingOutcomesProps {
  chartData: PinnedSpendPointData[];
  fullChartData: PinnedSpendPointData[];
  businessAsUsual: PinnedSpendPointData | null;
  pinnedSpendPoints: Map<number, PinnedSpendPointData>;
  setPinnedSpendPoints: Dispatch<SetStateAction<Map<number, PinnedSpendPointData>>>;
  stackSeries: EfficientFrontierSeries[];
  displaySeries: EfficientFrontierSeries[];
  selectedModels: Set<string>;
  setSelectedModels: Dispatch<SetStateAction<Set<string>>>;
}

export const MarketingOutcomes = ({
  chartData,
  fullChartData,
  businessAsUsual,
  pinnedSpendPoints,
  setPinnedSpendPoints,
  stackSeries,
  displaySeries,
  selectedModels,
  setSelectedModels
}: MarketingOutcomesProps): JSX.Element => {
  const xDomainTicks = useMemo(() => {
    if (chartData.length === 0) {
      return [];
    }
    const budgets = chartData.map(d => d.totalBudget);
    const xMin = Math.min(...budgets);
    const xMax = Math.max(...budgets);
    const tickCount = 12;
    if (xMax === xMin) {
      return [xMin];
    }
    const step = (xMax - xMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => xMin + step * i);
  }, [chartData]);

  return (
    <div className='ef-marketing-outcomes-section'>
      <div className='ef-marketing-outcomes-chart-container'>
        {chartData.length > 0 && (
          <AutoSizer
            renderProp={({ width, height }) => (
              <ComposedChart
                data={chartData}
                width={width ?? 0}
                height={height ?? 0}
                style={{ overflow: 'visible' }}
                onClick={(data: any) => {
                  if (data && data.activeLabel !== undefined) {
                    const clickedBudget = data.activeLabel as number;
                    if (clickedBudget === businessAsUsual?.totalBudget) {
                      return;
                    }
                    // Use fullChartData to get complete revenue data for all models
                    const dataPoint = fullChartData.find(d => d.totalBudget === clickedBudget);
                    if (dataPoint) {
                      setPinnedSpendPoints(prev => {
                        const newMap = new Map(prev);
                        if (newMap.has(clickedBudget)) {
                          newMap.delete(clickedBudget);
                        } else {
                          newMap.set(clickedBudget, dataPoint);
                        }
                        return newMap;
                      });
                    }
                  }
                }}
                margin={{ top: 30 }}
              >
                <XAxis
                  dataKey='totalBudget'
                  type='number'
                  domain={[xDomainTicks[0], xDomainTicks[xDomainTicks.length - 1]]}
                  ticks={xDomainTicks}
                  tickFormatter={value => `$${abbreviateNumber(value, 1)}`}
                  axisLine={{ stroke: GridColor }}
                  tick={{ fontSize: 12, fontWeight: 400, fill: AxisColor }}
                />
                <YAxis
                  yAxisId='left'
                  tickFormatter={value => `$${abbreviateNumber(value, 1)}`}
                  type='number'
                  domain={[0, 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fontWeight: 400, fill: AxisColor }}
                  label={{
                    value: 'Revenue',
                    position: 'insideTopLeft',
                    dy: -35,
                    dx: 30,
                    style: { fontSize: 12, fontWeight: 500, fill: AxisColor, textAnchor: 'middle' }
                  }}
                />
                <YAxis
                  yAxisId='right'
                  orientation='right'
                  tickFormatter={value => formatNumber(value, 2)}
                  type='number'
                  domain={[0, 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fontWeight: 400, fill: AxisColor }}
                  label={{
                    value: 'ROAS',
                    position: 'insideTopRight',
                    dy: -35,
                    dx: -35,
                    style: { fontSize: 12, fontWeight: 500, fill: AxisColor, textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop closes over parent state/props (selectedModels, businessAsUsual, pinnedSpendPoints, displaySeries); hoisting would require threading all of them through
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const spend = Number(label);
                      const revenue = Number(
                        payload.find(entry => entry.dataKey === 'totalHolisticRevenue')?.value
                      );
                      const roas = Number(
                        payload.find(entry => entry.dataKey === 'totalHolisticRoas')?.value
                      );
                      const isBau = spend === businessAsUsual?.totalBudget;
                      const spendDiff = getPercentageDifferenceIndicator(
                        spend,
                        businessAsUsual?.totalBudget || 0,
                        isBau,
                        true
                      );
                      const revenueDiff = getPercentageDifferenceIndicator(
                        revenue,
                        businessAsUsual?.totalHolisticRevenue || 0,
                        isBau
                      );
                      const roasDiff = getPercentageDifferenceIndicator(
                        roas,
                        businessAsUsual?.totalHolisticRoas || 0,
                        isBau
                      );
                      return (
                        <div className='ef-marketing-outcomes-tooltip-container'>
                          <div className='ef-tooltip-spend'>
                            <span className='ef-tooltip-spend-label'>Spend</span>
                            <span className='ef-tooltip-spend-value'>
                              {abbreviateScenarioValue(spend, { monetary: true })}
                            </span>
                            <div className='ef-tooltip-spend-indicator'>
                              {isBau && <span className='ef-bau-indicator'>BAU</span>}
                              {spendDiff.percentChange !== null && (
                                <PercentChange
                                  stretchMode={StretchMode.FIT}
                                  percentChange={spendDiff.percentChange}
                                  size={Size.SM}
                                />
                              )}
                            </div>
                          </div>
                          <div className='ef-tooltip-revenue'>
                            <div className='ef-tooltip-revenue-label-wrapper'>
                              <div
                                className={cn('legend-icon', 'simple-icon', {
                                  dash: true
                                })}
                                style={{
                                  backgroundColor: TotalHolisticRevenueColor
                                }}
                              />
                              <span className='ef-tooltip-revenue-label'>Revenue</span>
                            </div>
                            <span className='ef-tooltip-revenue-value'>
                              {abbreviateScenarioValue(revenue, { monetary: true })}
                            </span>
                            <div className='ef-tooltip-revenue-indicator'>
                              {revenueDiff.percentChange !== null && (
                                <PercentChange
                                  stretchMode={StretchMode.FIT}
                                  percentChange={revenueDiff.percentChange}
                                  size={Size.SM}
                                />
                              )}
                            </div>
                          </div>
                          <div className='ef-tooltip-revenue-breakout'>
                            {displaySeries
                              .filter(series => selectedModels.has(series.id))
                              .map(series => {
                                const seriesPayload = payload.find(
                                  entry => entry.name === series.id
                                );
                                if (!seriesPayload) {
                                  return null;
                                }
                                const seriesDiff = getPercentageDifferenceIndicator(
                                  Number(seriesPayload.value) || 0,
                                  businessAsUsual?.revenueBySlot?.[series.id] || 0,
                                  isBau
                                );
                                return (
                                  <Fragment key={series.id}>
                                    <div className='ef-tooltip-revenue-breakout-item-label-wrapper'>
                                      <div
                                        className={cn('legend-icon', 'simple-icon', {
                                          dash: false
                                        })}
                                        style={{ backgroundColor: series.color }}
                                      />
                                      <span className='ef-tooltip-revenue-breakout-item-label'>
                                        {series.label}
                                      </span>
                                    </div>
                                    <div className='ef-tooltip-revenue-breakout-item-value'>
                                      {abbreviateScenarioValue(Number(seriesPayload.value), {
                                        monetary: true
                                      })}
                                    </div>
                                    <div className='ef-tooltip-revenue-breakout-item-indicator'>
                                      {seriesDiff.indicatorText && (
                                        <span className={seriesDiff.indicatorClassName}>
                                          {seriesDiff.indicatorText}
                                        </span>
                                      )}
                                    </div>
                                  </Fragment>
                                );
                              })}
                          </div>
                          <div className='ef-tooltip-roas'>
                            <div className='ef-tooltip-roas-label-wrapper'>
                              <div
                                className={cn('legend-icon', 'simple-icon', {
                                  dash: true
                                })}
                                style={{
                                  backgroundColor: ROASColor
                                }}
                              />
                              <span className='ef-tooltip-roas-label'>ROAS</span>
                            </div>
                            <span className='ef-tooltip-roas-value'>{formatNumber(roas, 2)}</span>
                            <div className='ef-tooltip-roas-indicator'>
                              {roasDiff.percentChange !== null && (
                                <PercentChange
                                  stretchMode={StretchMode.FIT}
                                  percentChange={roasDiff.percentChange}
                                  size={Size.SM}
                                />
                              )}
                            </div>
                          </div>
                          {!isBau && <div className='ef-tooltip-delta-label'>%Δ vs BAU</div>}
                          {!isBau && (
                            <div className='ef-tooltip-footer'>
                              <MdPushPin />
                              <span className='ef-tooltip-footer-label'>
                                {pinnedSpendPoints.has(Number(spend))
                                  ? 'Click to un-pin spend level'
                                  : 'Click to pin spend level'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {stackSeries
                  .filter(series => selectedModels.has(series.id))
                  .map(series => (
                    <Area
                      key={series.id}
                      type='monotone'
                      dataKey={(point: PinnedSpendPointData) => point.revenueBySlot?.[series.id]}
                      stackId='1'
                      stroke={series.color}
                      fill={series.color}
                      name={series.id}
                      yAxisId='left'
                      isAnimationActive={false}
                    />
                  ))}
                <Line
                  type='monotone'
                  dataKey='totalHolisticRevenue'
                  stroke={TotalHolisticRevenueColor}
                  strokeWidth={2}
                  dot={false}
                  name='Total Holistic'
                  yAxisId='left'
                  isAnimationActive={false}
                />
                <Line
                  type='monotone'
                  dataKey='totalHolisticRoas'
                  stroke={ROASColor}
                  strokeWidth={2}
                  dot={false}
                  name='ROAS'
                  yAxisId='right'
                  isAnimationActive={false}
                />
                {Array.from(pinnedSpendPoints.keys()).map(budget => {
                  const isBau = budget === businessAsUsual?.totalBudget;
                  const lineColor = isBau ? AxisColor : PinnedSpendLineColor;
                  return (
                    <ReferenceLine
                      key={budget}
                      x={budget}
                      stroke={lineColor}
                      strokeDasharray='5 5'
                      strokeWidth={1}
                      yAxisId='left'
                      ifOverflow='extendDomain'
                      label={{
                        value: `$${(budget / 1000000).toFixed(1)}M`,
                        position: 'top',
                        fill: lineColor,
                        fontSize: 12,
                        fontWeight: 400
                      }}
                    />
                  );
                })}
                <CartesianGrid stroke={GridColor} strokeDasharray='0' vertical={false} />
              </ComposedChart>
            )}
          />
        )}
      </div>
      <MarketingOutcomesLegend
        series={displaySeries}
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
      />
    </div>
  );
};
